// Inches as an architectural fraction (`14 ½"`, `⅝"`, `30"`), to the nearest
// eighth. Pure — in shared-lib so a cabinet line the books estimate prices on
// the functions side reads the same width as the web app's readouts
// (web-app/src/utils/dimensionFormat.js, which builds on this). Moved there
// when a 0.26" filler was billed as `0" Cabinet Filler` (2026-10-20).

const EIGHTHS_PER_INCH = 8;

const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));

/**
 * Split a whole number of eighth-inches into inches plus a reduced fraction.
 * The caller has already rounded, so the carry is done: 96 eighths is
 * `{ whole: 12, numerator: 0 }`, never `{ whole: 11, numerator: 8 }`.
 */
const splitEighths = (totalEighths) => {
  const whole = Math.floor(totalEighths / EIGHTHS_PER_INCH);
  const eighths = totalEighths - whole * EIGHTHS_PER_INCH;
  if (eighths === 0) return { whole, numerator: 0, denominator: 1 };
  const divisor = gcd(eighths, EIGHTHS_PER_INCH);
  return {
    whole,
    numerator: eighths / divisor,
    denominator: EIGHTHS_PER_INCH / divisor,
  };
};

/**
 * The eight fractions an eighth-inch readout can produce, as single typographic
 * glyphs. `34 3/8"` runs together — the "3/8" reads as more digits of the
 * number — while `34 ⅜"` separates at a glance, because a vulgar fraction is
 * drawn smaller and stacked. Every denominator we emit (2, 4, 8) has one.
 */
const VULGAR = {
  '1/8': '⅛',
  '1/4': '¼',
  '3/8': '⅜',
  '1/2': '½',
  '5/8': '⅝',
  '3/4': '¾',
  '7/8': '⅞',
};

/** `0` -> "0", `4 1/2` -> "4 ½", `1/2` -> "½" (no leading zero). */
const inchesText = ({ whole, numerator, denominator }) => {
  if (numerator === 0) return `${whole}`;
  const frac = VULGAR[`${numerator}/${denominator}`];
  if (whole === 0) return frac;
  return `${whole} ${frac}`;
};

/** Inches rounded to the nearest eighth, as a number: 0.593 -> 0.625. */
const roundToEighth = (inches) =>
  Math.round(inches * EIGHTHS_PER_INCH) / EIGHTHS_PER_INCH;

/**
 * A value ALREADY in inches as a readout: `30"`, `14 ½"`, `½"`; '—' for
 * anything that is not a finite number.
 */
const formatInchesValue = (inches) => {
  if (typeof inches !== 'number' || !Number.isFinite(inches)) return '—';
  const sign = inches < 0 ? '-' : '';
  const totalEighths = Math.round(Math.abs(inches) * EIGHTHS_PER_INCH);
  return `${sign}${inchesText(splitEighths(totalEighths))}"`;
};

module.exports = {
  VULGAR,
  EIGHTHS_PER_INCH,
  splitEighths,
  inchesText,
  roundToEighth,
  formatInchesValue,
};
